import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, distinctUntilChanged, switchMap, take, takeUntil, tap } from 'rxjs';

import { Company } from '@site-mate/dashpivot-shared-library';
import { IAccount } from '@site-mate/sitemate-global-shared';

import { GlobalApiService } from 'app/global-api/global-api.service';
import { CompanyService } from 'app/shared/service/company.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { LastVisitedHomeService } from 'app/shared/service/last-visited-home.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

interface ISecuritySettingsForm {
  ssoEnabled: boolean;
}

@Component({
  selector: 'cc-workspace-sms-security-settings',
  templateUrl: './workspace-sms-security-settings.component.html',
  styleUrls: ['./workspace-sms-security-settings.component.scss'],
})
export class WorkspaceSmsSecuritySettingsComponent implements OnInit, OnDestroy {
  private unsubscribe = new Subject<void>();

  isSuperAdmin: boolean;
  isLoading = true;
  isSaving = false;
  company: Company;
  account: IAccount;

  formModel: ISecuritySettingsForm = {
    ssoEnabled: false,
  };

  @ViewChild('form') ngSecurityForm: NgForm;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly errorHandler: ErrorHandler,
    private readonly userService: UserService,
    private readonly companyService: CompanyService,
    private readonly globalApiService: GlobalApiService,
    private readonly toastrService: ToastrService,
    private readonly lastVisitedHomeService: LastVisitedHomeService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.setupModel();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  setupModel() {
    this.isSuperAdmin = this.userService.isAdmin();

    this.activatedRoute.params
      .pipe(
        distinctUntilChanged(),
        switchMap((params) => this.initCompany(params.companyId)),
        switchMap((company) => this.initAccount(company.accountId)),
        takeUntil(this.unsubscribe),
      )
      .subscribe({
        next: (account) => {
          this.formModel = this.buildSecuritySettingsForm(account);

          this.isLoading = false;
        },
        error: (error) => {
          this.errorHandler.handle(error);
          this.lastVisitedHomeService
            .goToFirstPossibleTeam(this.router, true)
            .pipe(takeUntil(this.unsubscribe));
        },
      });
  }

  initCompany(companyId: string) {
    return this.companyService.getCompany(companyId).pipe(
      tap((company) => {
        this.company = company;
      }),
    );
  }

  initAccount(accountId: string) {
    return this.globalApiService.getAccount(accountId).pipe(
      tap((account) => {
        this.account = account;
      }),
    );
  }

  ssoEnabledToggleClicked() {
    this.formModel.ssoEnabled = !this.formModel.ssoEnabled;
  }

  get hasInputTogglesChanged(): boolean {
    if (!this.account) {
      return false;
    }

    const hasSSOEnabledChanged = this.formModel.ssoEnabled !== this.account.ssoEnabled;

    return hasSSOEnabledChanged;
  }

  get canSaveSettings() {
    const hasFormChanged = this.ngSecurityForm?.form.dirty || this.hasInputTogglesChanged;

    return this.isSuperAdmin && hasFormChanged;
  }

  onSubmit(event: Event): void {
    event.preventDefault();

    if (!this.canSaveSettings) {
      return;
    }

    this.isSaving = true;

    this.globalApiService
      .updateAccount(this.account._id, {
        ssoEnabled: this.formModel.ssoEnabled,
      })
      .pipe(take(1))
      .subscribe({
        next: (account) => {
          this.account = account;
          this.toastrService.successByKey('sitemateStartSettingsUpdateSuccess');
          this.isSaving = false;
        },
        error: (error) => {
          this.isSaving = false;
          error.toastKey = 'sitemateStartSettingsUpdateFailed';
          this.errorHandler.handle(error);
        },
      });
  }

  private buildSecuritySettingsForm(account: IAccount): ISecuritySettingsForm {
    const form: ISecuritySettingsForm = {
      ssoEnabled: account.ssoEnabled,
    };

    return form;
  }
}
